import { useEffect, useState } from 'react';
import { DashboardLoader } from "@/core/components/DashboardLoader";
import { ErrorDisplay } from "@/core/components/ErrorDisplay";
import { DashboardLayout } from './DashboardLayout';
import { useDashboardUIStore } from '../../stores/dashboard-ui-store';
import { useEnergyStore, rangesToUtilization } from '../../stores/energy-store';
import { useActorStore } from '../../stores/actor-store';
import { useOperatorStore } from '@/core/stores/operator-store';
import { SessionDetailBlade } from '@/features/sessions-report/components/SessionDetailBlade';
import { SessionGridRow } from '@/features/sessions-report/types/session-types';

/**
 * Main container organism that orchestrates all dashboard functionality
 * Follows: Component → Zustand Store pattern (no custom hooks)
 */
export function SiteManagementDashboardContainer() {
  
  const uiStore = useDashboardUIStore();
  
  const {
    siteData,
    isLoading: siteDataLoading,
    selectedActorId,
    loadConnectors,
    setSelectedActor,
    refreshData,
    setFiltersOnly,
    clearError,
    viewMode,
    connectorRows,
    filterChargerStatus,
    filterExcludeInactive,
    filterOcppConnected,
    setFilterChargerStatus,
    setFilterUtilizationRange,
    setFilterHideInactive,
    setFilterOcppConnected,
    operatorFilteredCount,
    operatorHasMore,
    isLoadingMore,
    loadNextPage,
  } = useEnergyStore();
  
  const {
    actors,
    isLoading: actorsLoading,
    error: actorsError,
    loadActorTree,
    setSelectedOperator: setSelectedActorOperator,
  } = useActorStore();

  const { selectedOperator } = useOperatorStore();

  // Session detail blade state
  const [selectedSession, setSelectedSession] = useState<SessionGridRow | null>(null);
  const [isSessionBladeOpen, setIsSessionBladeOpen] = useState(false);

  // Handler for clicking on a session ID
  const handleSessionClick = (sessionId: string | null, machineName?: string, machineId?: number) => {
    if (!sessionId) return;

    // Create minimal SessionGridRow object for the blade
    const sessionRow: SessionGridRow = {
      sessionId: parseInt(sessionId),
      locationId: 0, // Will be loaded from API
      locationName: machineName || 'Unknown Machine',
      kioskActorHierarchy: '',
      startTime: '',
      stopTime: null,
      totalEnergy: null,
      settlementValue: null,
      isVirtualSession: false,
      machineId: machineId || 0,
      connectorId: 0,
      paymentId: null,
      durationInSeconds: null,
      currencyName: null,
      cost: null,
      vatCost: null,
      meterStart: null,
      meterStop: null,
      stopReason: null,
      startSource: null,
      accountName: null,
      cardType: null,
      rfid: null,
      connectionFee: null,
      timeFee: null,
    };

    setSelectedSession(sessionRow);
    setIsSessionBladeOpen(true);
  };

  const handleCloseSessionBlade = () => {
    setIsSessionBladeOpen(false);
    setSelectedSession(null);
  };

  // Initialize responsive behavior
  useEffect(() => {
    uiStore.initializeTreeWidth();
    
    const handleResize = () => {
      const newScreenSize = window.innerWidth;
      uiStore.updateResponsiveWidth(newScreenSize);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset auto-scroll flag after it's been used
  useEffect(() => {
    if (uiStore.shouldAutoScrollTree) {
      const timeout = setTimeout(() => {
        uiStore.setShouldAutoScrollTree(false);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [uiStore.shouldAutoScrollTree]);

  // Update selected actor when selectedLocationActorId changes
  useEffect(() => {
    if (selectedActorId !== uiStore.selectedLocationActorId) {
      setSelectedActor(uiStore.selectedLocationActorId);
    }
  }, [uiStore.selectedLocationActorId, selectedActorId]);

  // Load actor tree when operator is selected
  useEffect(() => {
    if (selectedOperator?.operatorId) {
      setSelectedActorOperator(selectedOperator.operatorId.toString());
      loadActorTree(selectedOperator.operatorId.toString());
    }
  }, [selectedOperator?.operatorId]);

  // Load data when actor and operator are available
  // Empty string '' means "All Locations" → operator-wide paginated view
  // Non-empty string means specific actor → single-site view (all connectors, no pagination)
  useEffect(() => {
    if (selectedOperator?.operatorId) {
      // Sync persisted uiStore filters into energyStore before loading.
      // This is needed after page reload since energyStore filter params are not persisted.
      const { min, max } = rangesToUtilization(uiStore.selectedAllocatedRanges);
      setFiltersOnly({ excludeInactive: uiStore.hideInactive, minUtilization: min, maxUtilization: max });

      const actorId = uiStore.selectedLocationActorId || undefined;
      loadConnectors(selectedOperator.operatorId.toString(), actorId);
    }
  }, [uiStore.selectedLocationActorId, selectedOperator?.operatorId, loadConnectors]);

  // Handle tree resize with proper constraints
  const handleTreeResize = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    uiStore.setIsResizing(true);
    const startX = e.clientX;
    const startWidth = uiStore.treeViewWidth;
    
    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      
      const deltaX = e.clientX - startX;
      
      // Responsive constraints based on screen size
      const minWidth = uiStore.screenSize < 1200 ? 200 : 250;
      const maxWidthPercent = uiStore.screenSize < 1200 ? 0.25 : 0.4;
      const maxWidth = Math.floor(uiStore.screenSize * maxWidthPercent);
      
      const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + deltaX));
      uiStore.setTreeViewWidth(newWidth);
    };
    
    const handleMouseUp = (e: MouseEvent) => {
      e.preventDefault();
      uiStore.setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.body.style.pointerEvents = 'none';
  };

  // Show loading state when actors are loading (initial load only)
  if (actorsLoading && !actors.length) {
    return <DashboardLoader />;
  }

  // Show error state for actors
  if (actorsError) {
    return (
      <div className="p-6 max-w-full mx-auto">
        <div className="flex items-center justify-center py-16">
          <ErrorDisplay 
            title="Failed to Load Operators"
            message="Unable to fetch operators from the server. Please try again later."
          />
        </div>
      </div>
    );
  }

  // Main render - show dashboard with subtle loading indicators
  return (
    <>
      <DashboardLayout
        uiStore={uiStore}
        actors={actors}
        siteData={siteData}
        selectedOperator={selectedOperator}
        selectedActorId={selectedActorId}
        onTreeResize={handleTreeResize}
        onRefreshData={refreshData}
        onClearError={clearError}
        isLoading={siteDataLoading}
        viewMode={viewMode}
        operatorStatusFilter={filterChargerStatus}
        operatorOnlineOnly={filterExcludeInactive}
        operatorOcppConnected={filterOcppConnected === true}
        onStatusFilterChange={setFilterChargerStatus}
        onUtilizationRangeChange={setFilterUtilizationRange}
        onOnlineOnlyChange={setFilterHideInactive}
        onOcppConnectedChange={setFilterOcppConnected}
        totalCount={viewMode === 'operator' ? operatorFilteredCount : undefined}
        onSessionClick={handleSessionClick}
        onLoadMore={viewMode === 'operator' ? loadNextPage : undefined}
        hasMore={viewMode === 'operator' ? operatorHasMore : false}
        isLoadingMore={isLoadingMore}
      />

      {/* Reusable Session Detail Blade */}
      <SessionDetailBlade
        session={selectedSession}
        isOpen={isSessionBladeOpen}
        onClose={handleCloseSessionBlade}
        unitMode={uiStore.unitMode}
      />
    </>
  );
}
